import React, { useEffect, useState } from "react";
import integrationsService from "../../../services/integrationsService";
import SourceScopeEditor from "../SourceScopeEditor";
import LoadingSpinner from "../../common/LoadingSpinner";
import { formatInUserTimezone } from "../../../utils/timezone";

const AVAILABLE_SOURCES = ["Chameleon", "FavorDrop", "DFC", "None"];
const AVAILABLE_EVENTS = [
  "lead.created",
  "lead.status_changed",
  "lead.assigned",
  "lead.next_activity_created",
  "lead.next_activity_overdue",
];

function SubscriptionsTab() {
  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState([]);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);
  const [created, setCreated] = useState(null);
  const [renewed, setRenewed] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await integrationsService.listSubscriptions();
      setSubs(Array.isArray(res) ? res : []);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load subscriptions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () =>
    setDialog({
      label: "",
      callbackUrl: "",
      eventTypes: ["lead.created", "lead.status_changed"],
      scopeMode: "all",
      scopeSources: [],
    });

  const toggleEvent = (e) => {
    setDialog((d) => {
      const eventTypes = d.eventTypes.includes(e)
        ? d.eventTypes.filter((x) => x !== e)
        : [...d.eventTypes, e];
      return { ...d, eventTypes };
    });
  };

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const allowedLeadSources =
        dialog.scopeMode === "all"
          ? null
          : dialog.scopeMode === "none"
            ? []
            : dialog.scopeSources;

      const res = await integrationsService.createSubscription({
        label: dialog.label,
        callbackUrl: dialog.callbackUrl,
        eventTypes: dialog.eventTypes,
        allowedLeadSources,
      });
      setCreated(res);
      setDialog(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to create subscription");
    } finally {
      setSaving(false);
    }
  };

  const renew = async (id) => {
    try {
      const res = await integrationsService.renewSubscription(id);
      setRenewed(res);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to renew token");
    }
  };

  const suspend = async (id) => {
    if (!window.confirm("Suspend this subscription? Webhooks will stop.")) return;
    try {
      await integrationsService.suspendSubscription(id);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to suspend");
    }
  };

  const terminate = async (id) => {
    if (!window.confirm("Terminate this subscription? This cannot be undone.")) return;
    try {
      await integrationsService.terminateSubscription(id);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to terminate");
    }
  };

  if (loading) return <LoadingSpinner message="Loading subscriptions..." />;

  return (
    <div>
      <div className="management-header">
        <div>
          <h2>Webhook Subscriptions</h2>
          <p className="text-muted">
            CRM pushes events to subscribed external systems.
          </p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>
          <i className="fas fa-plus" /> Create Subscription
        </button>
      </div>

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle" /> {error}
        </div>
      )}

      {subs.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-satellite-dish" />
          <h3>No subscriptions yet</h3>
          <p>Create one to push CRM events to an external system.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Label</th>
                <th>Callback</th>
                <th>Events</th>
                <th>Source Scope</th>
                <th>Token Expires</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.id}>
                  <td><strong>{s.label}</strong></td>
                  <td style={{ maxWidth: 220, wordBreak: "break-all" }}>
                    {s.callback_url}
                  </td>
                  <td>{(s.event_types || []).length} events</td>
                  <td>
                    {s.allowed_lead_sources === null ||
                    s.allowed_lead_sources === undefined
                      ? "All"
                      : Array.isArray(s.allowed_lead_sources) &&
                          s.allowed_lead_sources.length === 0
                        ? "None"
                        : s.allowed_lead_sources.join(", ")}
                  </td>
                  <td>
                    {s.token_expires_at
                      ? formatInUserTimezone(s.token_expires_at)
                      : "—"}
                  </td>
                  <td>
                    <span className={`status-badge ${s.status === "active" ? "active" : "inactive"}`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="actions">
                    {s.status !== "terminated" && (
                      <>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => renew(s.id)}
                        >
                          <i className="fas fa-sync-alt" /> Renew Token
                        </button>
                        {s.status === "active" && (
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => suspend(s.id)}
                          >
                            <i className="fas fa-pause" /> Suspend
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => terminate(s.id)}
                        >
                          <i className="fas fa-ban" /> Terminate
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialog && (
        <div className="modal-overlay" onMouseDown={() => !saving && setDialog(null)}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <h2>Create Webhook Subscription</h2>

            <div className="form-group">
              <label>Label *</label>
              <input
                className="form-control"
                value={dialog.label}
                onChange={(e) => setDialog({ ...dialog, label: e.target.value })}
                placeholder="e.g. Partner XYZ — Lead lifecycle"
              />
            </div>

            <div className="form-group">
              <label>Callback URL * (HTTPS only)</label>
              <input
                className="form-control"
                value={dialog.callbackUrl}
                onChange={(e) =>
                  setDialog({ ...dialog, callbackUrl: e.target.value })
                }
                placeholder="https://partner-xyz.example/crm-events"
              />
            </div>

            <div className="form-group">
              <label>Event Types</label>
              <div className="scope-checkbox-row">
                {AVAILABLE_EVENTS.map((ev) => (
                  <label key={ev} className="source-scope-item">
                    <input
                      type="checkbox"
                      checked={dialog.eventTypes.includes(ev)}
                      onChange={() => toggleEvent(ev)}
                    />
                    <span>{ev}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Source Scope</label>
              <SourceScopeEditor
                mode={dialog.scopeMode}
                sources={dialog.scopeSources}
                available={AVAILABLE_SOURCES}
                onChange={({ mode, sources }) =>
                  setDialog({ ...dialog, scopeMode: mode, scopeSources: sources })
                }
                disabled={saving}
              />
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setDialog(null)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={submit}
                disabled={
                  saving ||
                  !dialog.label.trim() ||
                  !dialog.callbackUrl.trim() ||
                  !dialog.eventTypes.length
                }
              >
                {saving ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {created && (
        <div className="modal-overlay">
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <h2>Subscription Created</h2>
            <div className="source-scope-note">
              <i className="fas fa-exclamation-triangle" />
              <span>
                These credentials are shown <strong>only once</strong>. Copy them
                now and store them securely.
              </span>
            </div>

            <div className="form-group">
              <label>Signing Secret</label>
              <textarea
                className="form-control"
                rows="2"
                readOnly
                value={created.signingSecret}
                onFocus={(e) => e.target.select()}
              />
            </div>

            <div className="form-group">
              <label>Access Token</label>
              <textarea
                className="form-control"
                rows="2"
                readOnly
                value={created.accessToken}
                onFocus={(e) => e.target.select()}
              />
            </div>

            <div className="form-group">
              <label>Expires</label>
              <input
                className="form-control"
                readOnly
                value={formatInUserTimezone(created.tokenExpiresAt)}
              />
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  navigator.clipboard.writeText(
                    `Signing Secret:\n${created.signingSecret}\n\nAccess Token:\n${created.accessToken}`,
                  );
                }}
              >
                <i className="fas fa-copy" /> Copy Both
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setCreated(null)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {renewed && (
        <div className="modal-overlay">
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <h2>Token Renewed</h2>
            <div className="source-scope-note">
              <i className="fas fa-info-circle" />
              <span>
                New token valid until{" "}
                <strong>{formatInUserTimezone(renewed.tokenExpiresAt)}</strong>.
                Old token remains valid for a short grace period.
              </span>
            </div>
            <div className="form-group">
              <label>New Access Token</label>
              <textarea
                className="form-control"
                rows="2"
                readOnly
                value={renewed.accessToken}
                onFocus={(e) => e.target.select()}
              />
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={() => setRenewed(null)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubscriptionsTab;